/**
 * Vision chat — explicit LOADING / ERROR / EMPTY / LOADED states; null-safe.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { SparklesIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import ConversationFeed from '../common/ConversationFeed';
import StrategyCardContent from '../common/StrategyCardContent';
import VisionIntensitySpectrum from './VisionIntensitySpectrum';
import AuraFocusStrip from '../advice/AuraFocusStrip';
import { useAdviceSync } from '../../context/AdviceSyncContext';
import { stripThinkingClient } from '../common/AuraFormattedText';

const VIEW = {
  LOADING: 'loading',
  ERROR: 'error',
  EMPTY: 'empty',
  LOADED: 'loaded',
};

const normalizeStrategyCard = (achievementStrategy) => {
  if (!achievementStrategy) return null;
  if (achievementStrategy.card) return achievementStrategy.card;
  const structured = achievementStrategy.structured;
  if (structured?.steps?.length) {
    const steps = structured.steps.map((step, idx) => {
      if (typeof step === 'string') {
        return { order: idx + 1, title: step, detail: '' };
      }
      return {
        order: step.order ?? idx + 1,
        title: step.title || step.detail || String(step),
        detail: step.detail || '',
        target_date: step.target_date,
      };
    });
    return {
      type: 'strategy',
      title: structured.title || '3-month strategy',
      horizon: structured.horizon || '3 months',
      steps,
      rationale: structured.rationale || '',
    };
  }
  return null;
};

const isFallbackResponse = (data) => {
  const route = (data?.route_info || '').toLowerCase();
  if (route.includes('fallback') || route.includes('timeout') || route.includes('noclient')) {
    return true;
  }
  const text = (data?.response || '').trim();
  return !text;
};

const mapStoredMessage = (msg) => ({
  id: msg?.message_id || `msg_${msg?.created_at || Date.now()}`,
  type: msg?.type === 'user' ? 'user_message' : 'aura_reflection',
  title: msg?.type === 'user' ? undefined : msg?.is_opening ? 'Your starting path' : 'Aura response',
  content: stripThinkingClient(msg?.content || ''),
  timestamp: msg?.created_at || new Date().toISOString(),
  follow_up_questions: msg?.follow_up_questions || [],
  advice_payload: msg?.advice_payload || null,
  intensity_spectrum: msg?.intensity_spectrum || null,
  spectrum_meta: msg?.spectrum_meta || null,
  is_opening: Boolean(msg?.is_opening),
});

const AuraVisionChat = ({
  vision,
  user,
  portfolioData: _portfolioData,
  achievementStrategy,
  onStrategyUpdated,
}) => {
  const visionId = vision?.vision_id;
  const [cards, setCards] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [viewState, setViewState] = useState(VIEW.LOADING);
  const [loadError, setLoadError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [selectedIntensity, setSelectedIntensity] = useState('incremental');
  const [strategyCard, setStrategyCard] = useState(() =>
    normalizeStrategyCard(achievementStrategy || vision?.achievement_strategy),
  );
  const { applySyncPayload, refreshAfterChat } = useAdviceSync();

  useEffect(() => {
    setStrategyCard(
      normalizeStrategyCard(achievementStrategy || vision?.achievement_strategy),
    );
  }, [achievementStrategy, vision?.achievement_strategy]);

  const loadChat = useCallback(async (cancelRef) => {
    if (!visionId) {
      setViewState(VIEW.EMPTY);
      setLoadError('No vision selected.');
      return;
    }

    setViewState(VIEW.LOADING);
    setLoadError(null);

    try {
      const historyRes = await axios.get(`/api/visions/${visionId}/chat`);
      if (cancelRef.cancelled) return;

      const stored = historyRes.data?.messages || [];
      if (stored.length > 0) {
        const mapped = stored.map(mapStoredMessage);
        const opening = mapped.find((m) => m.is_opening);
        if (opening && !opening.intensity_spectrum) {
          try {
            const specRes = await axios.get(`/api/visions/${visionId}/chat/spectrum`);
            opening.intensity_spectrum = specRes.data?.intensity_spectrum;
            opening.spectrum_meta = specRes.data?.spectrum_meta;
          } catch {
            /* optional */
          }
        }
        setCards(mapped);
        setViewState(VIEW.LOADED);
        return;
      }

      const openingRes = await axios.get(`/api/visions/${visionId}/chat/opening`);
      if (cancelRef.cancelled) return;

      if (isFallbackResponse(openingRes.data)) {
        const detail =
          openingRes.data?.detail ||
          'Aura could not generate your starting path. Check your connection and try again.';
        setLoadError(detail);
        setViewState(VIEW.ERROR);
        console.error('[AuraVisionChat] opening fallback or empty:', openingRes.data);
        return;
      }

      let spectrum = openingRes.data?.intensity_spectrum;
      let spectrumMeta = openingRes.data?.spectrum_meta;
      if (!spectrum) {
        try {
          const specRes = await axios.get(`/api/visions/${visionId}/chat/spectrum`);
          spectrum = specRes.data?.intensity_spectrum;
          spectrumMeta = specRes.data?.spectrum_meta;
        } catch {
          /* optional */
        }
      }

      setCards([
        {
          id: openingRes.data?.message_id || 'opening',
          type: 'aura_reflection',
          title: 'Your starting path',
          content: stripThinkingClient(openingRes.data?.response || ''),
          advice_payload: openingRes.data?.advice_payload || null,
          intensity_spectrum: spectrum || null,
          spectrum_meta: spectrumMeta || null,
          is_opening: true,
          timestamp: new Date().toISOString(),
        },
      ]);
      setViewState(VIEW.LOADED);
    } catch (err) {
      if (cancelRef.cancelled) return;
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        'Could not load the AI assistant.';
      console.error('[AuraVisionChat] load failed:', err);
      setLoadError(message);
      setViewState(VIEW.ERROR);
    }
  }, [visionId]);

  useEffect(() => {
    const cancelRef = { cancelled: false };
    loadChat(cancelRef);
    return () => {
      cancelRef.cancelled = true;
    };
  }, [loadChat, reloadToken]);

  const handleRetry = () => {
    setReloadToken((t) => t + 1);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading || !visionId) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setLoading(true);
    setViewState(VIEW.LOADED);

    const userCard = {
      id: `user_${Date.now()}`,
      type: 'user_message',
      content: userMessage,
      timestamp: new Date().toISOString(),
    };
    setCards((prev) => [...prev, userCard]);

    try {
      const response = await axios.post(`/api/visions/${visionId}/chat`, {
        message: userMessage,
        intensity: selectedIntensity,
      });

      if (isFallbackResponse(response.data)) {
        setLoadError('Aura returned a fallback response. Please try again.');
        setViewState(VIEW.ERROR);
        return;
      }

      if (response.data?.response) {
        const aiCard = {
          id: `ai_${response.data.message_id || Date.now()}`,
          type: 'aura_reflection',
          title: 'Aura response',
          content: stripThinkingClient(response.data.response),
          advice_payload: response.data.advice_payload || null,
          timestamp: new Date().toISOString(),
          follow_up_questions: response.data.follow_up_questions || [],
        };
        setCards((prev) => [...prev, aiCard]);

        if (response.data.advice_sync) {
          applySyncPayload(response.data.advice_sync);
        }

        if (response.data.strategy_updated) {
          const plan = response.data.strategy_updated;
          setStrategyCard(plan);
          onStrategyUpdated?.(plan);
          setCards((prev) => [
            ...prev,
            {
              id: `strategy_${Date.now()}`,
              type: 'strategy',
              title: plan.title,
              horizon: plan.horizon,
              steps: plan.steps,
              rationale: plan.rationale,
              timestamp: new Date().toISOString(),
            },
          ]);
        }

        await refreshAfterChat();
      }
    } catch (error) {
      console.error('[AuraVisionChat] send failed:', error);
      setCards((prev) => [
        ...prev,
        {
          id: `error_${Date.now()}`,
          type: 'aura_reflection',
          title: 'Something went wrong',
          content: 'Please try again in a moment.',
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const pinnedStrategy = useMemo(() => strategyCard, [strategyCard]);
  const isOpening = viewState === VIEW.LOADING;

  const renderBody = () => {
    if (viewState === VIEW.LOADING) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center py-16" aria-busy="true">
          <div className="w-10 h-10 border-2 border-hairline border-t-pine rounded-full animate-spin mb-4" />
          <p className="text-body text-secondary">Loading your AI assistant…</p>
        </div>
      );
    }

    if (viewState === VIEW.ERROR) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center py-12 px-4 text-center" role="alert">
          <p className="font-display text-h3 text-primary mb-2">Something went wrong</p>
          <p className="text-body text-secondary mb-6 max-w-md">{loadError}</p>
          <button type="button" onClick={handleRetry} className="btn-primary inline-flex items-center gap-2">
            <ArrowPathIcon className="w-4 h-4" />
            Retry
          </button>
        </div>
      );
    }

    if (viewState === VIEW.EMPTY) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center py-12 px-4 text-center">
          <SparklesIcon className="w-10 h-10 text-pine mb-4" />
          <p className="font-display text-h3 text-primary mb-2">Start your first chat</p>
          <p className="text-body text-secondary mb-4 max-w-md">
            Ask Aura about your goal, strategy, or next step. Generate a strategy on the Progress tab
            for the richest answers.
          </p>
        </div>
      );
    }

    return (
      <div className="flex-1 min-h-[280px] overflow-y-auto mb-4">
        <ConversationFeed
          cards={cards}
          user={user}
          section="vision"
          visionId={visionId}
          loading={loading}
          renderAfterCard={(card) =>
            card?.is_opening && card?.intensity_spectrum ? (
              <VisionIntensitySpectrum
                spectrum={card.intensity_spectrum}
                spectrumMeta={card.spectrum_meta}
                selectedIntensity={selectedIntensity}
                onSelectIntensity={(key, payload) => {
                  setSelectedIntensity(key);
                  if (payload?.verdict && !payload.suppressed) {
                    setCards((prev) =>
                      prev.map((c) =>
                        c.id === card.id
                          ? { ...c, advice_payload: { ...payload, intensity: key } }
                          : c,
                      ),
                    );
                  }
                }}
                visionId={visionId}
              />
            ) : null
          }
        />
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col min-h-[560px] light-surface">
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-hairline">
        <div className="w-10 h-10 rounded-md bg-pine flex items-center justify-center shrink-0">
          <SparklesIcon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="font-display text-h2 text-primary">Ask Aura</h2>
          <p className="text-body text-secondary">Anchored to your strategy and numbers</p>
        </div>
      </div>

      <AuraFocusStrip className="mb-4" compact />

      {pinnedStrategy && viewState === VIEW.LOADED && (
        <div className="mb-4">
          <p className="text-label text-secondary mb-2">Your plan — add each week to calendar</p>
          <StrategyCardContent card={pinnedStrategy} visionId={visionId} showStepActions />
        </div>
      )}

      {renderBody()}

      <form onSubmit={handleSendMessage} className="flex gap-2 pt-4 border-t border-hairline">
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder={`Ask about your vision (${selectedIntensity} ambition)…`}
          className="flex-1 input-editorial"
          disabled={loading || isOpening || viewState === VIEW.ERROR || !visionId}
        />
        <button
          type="submit"
          disabled={
            !inputMessage.trim() || loading || isOpening || viewState === VIEW.ERROR || !visionId
          }
          className="btn-primary px-5 disabled:opacity-50"
        >
          {loading ? 'Sending…' : 'Send'}
        </button>
      </form>
    </div>
  );
};

export default AuraVisionChat;
